import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import "../../styles/LearningPlans.css";
import { signedAuthHeaders } from "../../api";
import { coverForCourse, coverForPlan } from "../../catalogCovers";

const LearningPlanDetails = () => {
    const { id } = useParams();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [buyMessage, setBuyMessage] = useState(null);
    const userWalletAddress = localStorage.getItem("user");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetch(`/api/learningplans/${id}`)
            .then(async (res) => {
                const text = await res.text();
                if (!res.ok) {
                    throw new Error(res.status === 404 ? "Learning Plan not found." : `Could not load plan (${res.status})`);
                }
                return JSON.parse(text);
            })
            .then((data) => {
                if (!cancelled) {
                    setPlan(data);
                    setError(null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setPlan(null);
                    setError(err.message || "Could not load learning plan");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) return <p className="lp-loading">Loading...</p>;
    if (error) return <p className="lp-error">{error}</p>;
    if (!plan) return <p className="lp-error">Learning Plan not found.</p>;

    const courses = Array.isArray(plan.courses) ? plan.courses : [];
    const assessments = Array.isArray(plan.assessments) ? plan.assessments : [];
    const courseTotal = courses.reduce((sum, c) => sum + (Number(c.price) || 0), 0);
    const assessmentTotal = assessments.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
    const fullPrice = plan.price != null ? Number(plan.price) : courseTotal + assessmentTotal;
    const discountedPrice = plan.discountedPrice != null
        ? Number(plan.discountedPrice).toFixed(2)
        : (fullPrice * 0.9).toFixed(2);

    const handleBuyPlan = async () => {
        if (!userWalletAddress) {
            setBuyMessage("Connect a wallet to enroll in this learning plan.");
            return;
        }
        try {
            const response = await fetch(`/api/learningplans/buy/${id}`, {
                method: "POST",
                headers: await signedAuthHeaders({ Accept: "application/json" }),
            });
            const text = await response.text();
            let data = {};
            try {
                data = text ? JSON.parse(text) : {};
            } catch (e) {
                data = { message: text };
            }
            if (!response.ok) {
                throw new Error(data.message || data.error || `Enroll failed (${response.status})`);
            }
            setBuyMessage(data.message || "Enrolled in this learning plan.");
        } catch (err) {
            setBuyMessage(err.message || "Could not enroll in this learning plan.");
        }
    };

    const handleClaimCertificate = async () => {
        if (!userWalletAddress) {
            setBuyMessage("Connect a wallet to claim this certificate.");
            return;
        }
        try {
            const response = await fetch(`/api/learningplans/${id}/claim`, {
                method: "POST",
                headers: await signedAuthHeaders({ Accept: "application/json" }),
            });
            const text = await response.text();
            let data = {};
            try {
                data = text ? JSON.parse(text) : {};
            } catch (e) {
                data = { message: text };
            }
            if (!response.ok) {
                throw new Error(data.message || data.error || `Claim failed (${response.status})`);
            }
            setBuyMessage(data.coursetitle
                ? `Certificate issued: ${data.coursetitle}.`
                : (data.message || "Certificate issued."));
        } catch (err) {
            setBuyMessage(err.message || "Could not claim this certificate.");
        }
    };

    return (
        <div className="lp-details-container">
            <div className="lp-header">
                <img className="lp-hero-cover" src={coverForPlan(plan)} alt="" />
                <h1>{plan.title}</h1>
                <p className="lp-subtitle">{plan.description}</p>
                <p className="lp-creator">Created by: <strong>{plan.createdBy}</strong></p>
            </div>

            <div className="lp-section">
                <h2>Courses in this plan</h2>
                {courses.length === 0 ? (
                    <p>No courses have been grouped into this plan yet.</p>
                ) : (
                    <ul className="lp-list">
                        {courses.map((course) => (
                            <li key={course.id}>
                                <img className="lp-item-thumb" src={coverForCourse(course)} alt="" />
                                <Link className="lp-item-title" to={`/courses/${course.id}`}>{course.title}</Link>
                                <span className="lp-price">{Number(course.price) || 0} WSD</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="lp-section">
                <h2>Assessments</h2>
                {assessments.length === 0 ? (
                    <p>No assessments attached to this plan.</p>
                ) : (
                    <ul className="lp-list">
                        {assessments.map((assessment) => (
                            <li key={assessment.id}>
                                <span className="lp-item-title">{assessment.title}</span>
                                <span className="lp-price">{Number(assessment.price) || 0} WSD</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="lp-summary">
                <p>Listed course total: <strong>{fullPrice.toFixed(2)} WSD</strong></p>
                <p>Plan price: <strong className="lp-discount">{discountedPrice} WSD</strong></p>
                <button className="lp-buy-button" onClick={handleBuyPlan}>
                    Enroll in this learning plan
                </button>
                <button className="lp-buy-button" onClick={handleClaimCertificate} style={{ marginLeft: "8px" }}>
                    Claim certificate
                </button>
                {buyMessage && <p className="lp-buy-message">{buyMessage}</p>}
            </div>
        </div>
    );
};

export default LearningPlanDetails;
